import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Container,
  Button,
  CircularProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Inventory2 as Inventory2Icon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Layout from '../../components/layout/Layout';
import ReportMetricCard from '../../components/reports/ReportMetricCard';
import { reportPageSx } from '../../components/reports/reportPageLayout';
import hsnReportService from '../../services/hsnReportService';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '../../utils/formatCurrency';
import * as XLSX from 'xlsx';

const HSNReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({
    rows: [],
    itemRows: [],
    summary: null
  });

  // Default to current month
  const getCurrentMonthDates = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date();
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const [startDate, setStartDate] = useState(getCurrentMonthDates().startDate);
  const [endDate, setEndDate] = useState(getCurrentMonthDates().endDate);
  const [hsnCodeSearch, setHsnCodeSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  const formatIgstAmountCell = (amount) => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return '-';
    return formatCurrency(n);
  };

  // Fetch report data
  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await hsnReportService.getHSNReport(startDate, endDate);
      setReportData(response);
    } catch (error) {
      console.error('Error fetching HSN report:', error);
      toast.error('Failed to load HSN report');
    } finally {
      setLoading(false);
    }
  };

  // Load report on mount and when dates change
  useEffect(() => {
    fetchReportData();
  }, [startDate, endDate]);

  const formatExcelDateRange = (start, end) => {
    const fmt = (iso) => {
      if (!iso) return '';
      const [y, m, d] = iso.split('-');
      return `${d}/${m}/${y}`;
    };
    return `From ${fmt(start)} to ${fmt(end)}`;
  };

  // Export to Excel — two sheets: HSN summary (same as table) + item-wise breakdown
  const handleExport = async () => {
    try {
      setIsExporting(true);

      const exportData = reportData.rows.map(row => ({
        'HSN Code': row.hsnCode,
        'GST %': `${row.gstRate}%`,
        'Taxable Value (₹)': row.taxableValue || 0,
        'Total Sold Products': row.totalSoldProducts || 0,
        'IGST Amount (₹)': Number(row.igst) > 0 ? Number(row.igst) : '-',
        'CGST Amount (₹)': row.cgst || 0,
        'SGST Amount (₹)': row.sgst || 0,
        'Total GST Amount (₹)': row.totalGSTAmount || 0,
        'Total Value Sold (₹)': row.totalValueSold || 0,
      }));

      if (reportData.summary) {
        exportData.push({});
        exportData.push({
          'HSN Code': 'TOTAL',
          'GST %': '',
          'Taxable Value (₹)': reportData.summary.totalTaxableValue || 0,
          'Total Sold Products': reportData.summary.totalSoldProducts || 0,
          'IGST Amount (₹)': Number(reportData.summary.totalIGST) > 0
            ? Number(reportData.summary.totalIGST)
            : '-',
          'CGST Amount (₹)': reportData.summary.totalCGST || 0,
          'SGST Amount (₹)': reportData.summary.totalSGST || 0,
          'Total GST Amount (₹)': reportData.summary.totalGSTAmount || 0,
          'Total Value Sold (₹)': reportData.summary.totalValueSold || 0,
        });
      }

      const wsHsn = XLSX.utils.aoa_to_sheet([
        [formatExcelDateRange(startDate, endDate)],
        ['HSN Wise Summary'],
        [],
      ]);
      if (exportData.length > 0) {
        XLSX.utils.sheet_add_json(wsHsn, exportData, { origin: 'A4' });
      } else {
        XLSX.utils.sheet_add_aoa(wsHsn, [['No data']], { origin: 'A4' });
      }

      const rawItemRows = reportData.itemRows || [];
      const itemExport = rawItemRows.map(row => ({
        'HSN Code': row.hsnCode,
        'Item Name': row.productName,
        'Total Qty': row.quantityLabel || '',
        'GST %': `${row.gstRate}%`,
        'Taxable Value (₹)': row.taxableValue || 0,
        'IGST Amount (₹)': Number(row.igst) > 0 ? Number(row.igst) : '-',
        'CGST Amount (₹)': row.cgst || 0,
        'SGST Amount (₹)': row.sgst || 0,
        'Total GST Amount (₹)': row.totalGSTAmount || 0,
        'Total Value Sold (₹)': row.totalValueSold || 0,
      }));

      if (reportData.summary) {
        itemExport.push({});
        itemExport.push({
          'HSN Code': 'TOTAL',
          'Item Name': '',
          'Total Qty': `Count ${rawItemRows.length}`,
          'GST %': '',
          'Taxable Value (₹)': reportData.summary.totalTaxableValue || 0,
          'IGST Amount (₹)': Number(reportData.summary.totalIGST) > 0
            ? Number(reportData.summary.totalIGST)
            : '-',
          'CGST Amount (₹)': reportData.summary.totalCGST || 0,
          'SGST Amount (₹)': reportData.summary.totalSGST || 0,
          'Total GST Amount (₹)': reportData.summary.totalGSTAmount || 0,
          'Total Value Sold (₹)': reportData.summary.totalValueSold || 0,
        });
      }

      const wsItems = XLSX.utils.aoa_to_sheet([
        [formatExcelDateRange(startDate, endDate)],
        ['Item Details'],
        [],
      ]);
      if (itemExport.length > 0) {
        XLSX.utils.sheet_add_json(wsItems, itemExport, { origin: 'A4' });
      } else {
        XLSX.utils.sheet_add_aoa(wsItems, [['No product-level lines for this period']], { origin: 'A4' });
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsHsn, 'HSN Summary');
      XLSX.utils.book_append_sheet(wb, wsItems, 'Item Details');

      const filename = `HSN_Report_${startDate}_to_${endDate}.xlsx`;
      XLSX.writeFile(wb, filename);

      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter rows by HSN code search
  const filteredRows = reportData.rows.filter(row => {
    if (!hsnCodeSearch.trim()) return true;
    return row.hsnCode && row.hsnCode.toLowerCase().includes(hsnCodeSearch.toLowerCase().trim());
  });

  // Paginated rows
  const paginatedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [hsnCodeSearch]);

  return (
    <Layout>
      <Container maxWidth="xl" sx={reportPageSx.container}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={reportPageSx.breadcrumbs}>
          <Link component={RouterLink} to="/reports" color="inherit">
            Reports
          </Link>
          <Typography color="text.primary">HSN Report</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <Typography variant="h4" sx={{ ...reportPageSx.pageTitle, m: 0 }}>
            HSN Wise Report
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={isExporting || loading || reportData.rows.length === 0}
          >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </Box>

        <Card sx={reportPageSx.filterCard}>
          <CardContent sx={reportPageSx.filterCardContent}>
            <Grid
              container
              spacing={reportPageSx.filterGrid.spacing}
              alignItems="flex-end"
              sx={{ flexWrap: { xs: 'wrap', lg: 'nowrap' } }}
            >
              <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0 }}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0 }}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={5} sx={{ minWidth: 0 }}>
                <TextField
                  fullWidth
                  label="Search HSN code"
                  size="small"
                  value={hsnCodeSearch}
                  onChange={(e) => setHsnCodeSearch(e.target.value)}
                  placeholder="Filter by HSN code"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0, flexShrink: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={fetchReportData}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                  sx={reportPageSx.filterButton}
                >
                  Generate
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {reportData.summary && (
          <Grid container spacing={reportPageSx.metricsGrid.spacing} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <ReportMetricCard
                label="HSN Codes"
                value={loading ? '…' : filteredRows.length}
                icon={<Inventory2Icon />}
                accentColor="primary.main"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ReportMetricCard
                label="Taxable Value"
                value={loading ? '…' : formatCurrency(reportData.summary.totalTaxableValue)}
                icon={<CurrencyRupeeIcon />}
                accentColor="warning.main"
                valueColor="warning.main"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ReportMetricCard
                label="Total GST"
                value={loading ? '…' : formatCurrency(reportData.summary.totalGSTAmount)}
                icon={<CurrencyRupeeIcon />}
                accentColor="info.main"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ReportMetricCard
                label="Total Sold Value"
                value={loading ? '…' : formatCurrency(reportData.summary.totalValueSold)}
                icon={<CurrencyRupeeIcon />}
                accentColor="success.main"
                valueColor="success.main"
              />
            </Grid>
          </Grid>
        )}

        <Card sx={reportPageSx.sectionCard}>
          <CardContent sx={{ ...reportPageSx.sectionCardContent, p: filteredRows.length ? 0 : undefined, '&:last-child': { pb: filteredRows.length ? 0 : undefined } }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredRows.length === 0 ? (
              <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                {reportData.rows.length === 0
                  ? 'No data available for the selected date range'
                  : 'No HSN codes found matching your search'}
              </Typography>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>HSN Code</TableCell>
                        <TableCell align="right">GST %</TableCell>
                        <TableCell align="right">Taxable Value</TableCell>
                        <TableCell align="right">Qty Sold</TableCell>
                        <TableCell align="right">IGST</TableCell>
                        <TableCell align="right">CGST</TableCell>
                        <TableCell align="right">SGST</TableCell>
                        <TableCell align="right">Total GST</TableCell>
                        <TableCell align="right">Total Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRows.map((row, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{row.hsnCode}</TableCell>
                          <TableCell align="right">{row.gstRate}%</TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.taxableValue)}
                          </TableCell>
                          <TableCell align="right">{row.totalSoldProducts}</TableCell>
                          <TableCell align="right">
                            {formatIgstAmountCell(row.igst)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.cgst || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.sgst || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.totalGSTAmount)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.totalValueSold)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {reportData.summary && (
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell colSpan={2} sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatCurrency(reportData.summary.totalTaxableValue)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {reportData.summary.totalSoldProducts}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatIgstAmountCell(reportData.summary.totalIGST)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatCurrency(reportData.summary.totalCGST || 0)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatCurrency(reportData.summary.totalSGST || 0)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatCurrency(reportData.summary.totalGSTAmount)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatCurrency(reportData.summary.totalValueSold)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  size="small"
                  count={filteredRows.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Layout>
  );
};

export default HSNReport;

